import { useMemo } from "react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { toast } from "sonner";
import { AlertTriangle, Bell, History, Loader2 } from "lucide-react";
import type { Vehicle } from "@/types";
import { useRecentEvents, useHistoryDeletions, useRestoreHistoryDeletion } from "@/hooks/queries";
import { usePermission } from "@/hooks/use-permission";
import { ALERT_TYPE_META, ALERT_TONE_CLASSES } from "@/lib/alert-types";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// Extraído de routes/vehicles.tsx (painel expandido da linha) pra
// reaproveitar também na aba Logs de Editar Veículo (pedido 2026-09-08:
// "quero aba de logs com tudo que aconteceu, o que foi enviado, o que foi
// recebido, qual erro deu, o que o veículo fez"). Fonte real: eventos do
// Traccar (mesma consulta do EventosPanel do PWA), que já cobre tanto
// telemetria do veículo (ignição, movimento, online/offline, alarme) quanto
// o ciclo de comando (queuedCommandSent = entregue ao dispositivo,
// commandResult = resposta crua do protocolo, ex. "RELAY,0#=Success!").
// SOMADO (mesmo pedido, 2026-09-08) aos registros de "Apagar histórico"
// (histórico de posição/rota ocultado por um cliente) — não vem do
// Traccar, vem do GPS Drive Backend (ver gpsdrive-history-deletions.
// service.ts). Administrador da empresa vê um botão "Recuperar" em cima
// dos ainda ativos.
//
// Isso NÃO inclui erros de envio que nunca chegaram a sair do navegador
// (ex.: Traccar recusou o POST /api/commands/send por comando não suportado
// pelo protocolo) — esses só aparecem como toast no momento do envio (ver
// VehicleSendCommandDialog); o Traccar não gera evento para uma tentativa
// que nem chegou a ser enfileirada.
export function VehicleLogsPanel({ vehicle, hours = 72 }: { vehicle: Vehicle; hours?: number }) {
  const deviceId = Number(vehicle.id);
  const { data: events = [], isLoading, isError } = useRecentEvents([vehicle.id], hours);
  const { data: deletions = [] } = useHistoryDeletions(deviceId);
  const isAdmin = usePermission("administrador");
  const restoreMutation = useRestoreHistoryDeletion(deviceId);

  const handleRestore = async (id: string) => {
    try {
      await restoreMutation.mutateAsync(id);
      toast.success("Histórico recuperado.");
    } catch {
      toast.error("Não foi possível recuperar o histórico agora.");
    }
  };

  const timeline = useMemo(() => {
    const eventItems = events.map((ev) => ({ kind: "event" as const, time: ev.eventTime, event: ev }));
    const deletionItems = deletions.map((d) => ({
      kind: "history-deletion" as const,
      time: d.createdAt,
      deletion: d,
    }));
    return [...eventItems, ...deletionItems].sort((a, b) => b.time.localeCompare(a.time));
  }, [events, deletions]);

  if (isLoading) {
    return (
      <div className="space-y-2 p-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-12 w-full animate-pulse rounded-lg bg-slate-100" />
        ))}
      </div>
    );
  }
  if (isError) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-rose-600">
        <AlertTriangle className="h-4 w-4 shrink-0" />
        Não foi possível carregar os logs deste veículo agora.
      </div>
    );
  }
  if (timeline.length === 0) {
    return (
      <div className="p-6 text-center text-sm text-slate-500">
        Nenhum evento registrado no período.
      </div>
    );
  }
  const days = Math.round(hours / 24);
  return (
    <div className="space-y-2">
      <p className="px-1 text-[11px] text-muted-foreground">
        Eventos reais do rastreador nos últimos {days} {days === 1 ? "dia" : "dias"} — inclui comandos
        enviados/respondidos (RELAY, etc.), ignição, movimento e alarmes. A resposta abaixo de um
        comando é o texto cru devolvido pelo próprio equipamento: confirma que o comando chegou e foi
        processado, não que a ação física (ex. destravar) de fato ocorreu. Registros de "Apagar
        histórico" (abaixo) valem para qualquer período, não só os últimos {days} dias.
      </p>
      <ul className="max-h-[420px] space-y-1.5 overflow-y-auto p-1">
        {timeline.map((item) => {
          if (item.kind === "history-deletion") {
            const d = item.deletion;
            const active = !d.restoredAt;
            return (
              <li
                key={`hd-${d.id}`}
                className={cn(
                  "flex items-start gap-3 rounded-lg border px-3 py-2",
                  active ? ALERT_TONE_CLASSES.red : ALERT_TONE_CLASSES.emerald,
                )}
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md border bg-white/60">
                  <History className="h-4 w-4" strokeWidth={2} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold">
                    {active ? "Histórico apagado" : "Histórico apagado (recuperado)"}
                  </div>
                  <div className="text-[11px] opacity-80">
                    Período: {d.periodLabel} — apagado por {d.deletedByName} em{" "}
                    {format(new Date(d.createdAt), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                  </div>
                  {!active && (
                    <div className="text-[11px] opacity-80">
                      Recuperado por {d.restoredByName} em{" "}
                      {format(new Date(d.restoredAt!), "dd/MM/yyyy HH:mm", { locale: ptBR })}
                    </div>
                  )}
                  {active && isAdmin && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="mt-1.5 h-7 bg-white/70 px-2 text-[11px]"
                      disabled={restoreMutation.isPending}
                      onClick={() => handleRestore(d.id)}
                    >
                      {restoreMutation.isPending ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : (
                        "Recuperar histórico"
                      )}
                    </Button>
                  )}
                </div>
              </li>
            );
          }

          const ev = item.event;
          const meta = ALERT_TYPE_META[ev.type] ?? { icon: Bell, label: ev.type, tone: "slate" as const };
          const resultText =
            ev.type === "commandResult" && typeof ev.attributes?.result === "string"
              ? (ev.attributes.result as string).trim()
              : undefined;
          const looksLikeError =
            !!resultText && /error|erro|fail|falha/i.test(resultText) && !/success/i.test(resultText);
          return (
            <li
              key={ev.id}
              className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2"
            >
              <span
                className={cn(
                  "flex h-8 w-8 shrink-0 items-center justify-center rounded-md border",
                  ALERT_TONE_CLASSES[meta.tone],
                )}
              >
                <meta.icon className="h-4 w-4" strokeWidth={2} />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-xs font-semibold text-slate-800">{meta.label}</div>
                <div className="text-[11px] text-slate-400">
                  {format(new Date(ev.eventTime), "dd/MM/yyyy HH:mm:ss", { locale: ptBR })}
                </div>
                {resultText && (
                  <div
                    className={cn(
                      "mt-1 rounded border px-1.5 py-1 font-mono text-[11px]",
                      looksLikeError
                        ? "border-red-200 bg-red-50 text-red-700"
                        : "border-slate-200 bg-slate-50 text-slate-700",
                    )}
                  >
                    {resultText}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
